import { api } from ".";

/**
 * VARDİYA TANIMI ve ATAMASI (plan 2.24).
 *
 * Takvim'in `shiftAssignmentApi.tsx` (`/shift/*`) modülüyle karıştırılmamalı:
 * orası işgücü planı ("17 Ağustos'ta Üretim'e 5 operatör lazım"), burası
 * çalışma düzeni ("Ahmet gündüz vardiyasında çalışır"). Bu modül, backend'de
 * hiç karşılığı olmayan eski `weeklyShiftApi.tsx`'in yerini aldı.
 */

const definitionPath = "/shift-definition";
const assignmentPath = "/shift-assignment";

/** Haftalık desenin tek günü — backend `shiftPattern.ShiftDay` ile birebir. */
export type ShiftDay = {
  dayOfWeek: number;
  startMinutes: number;
  workMinutes: number;
  breakMinutes?: number;
};

export type ShiftDefinition = {
  _id: string;
  name: string;
  code: string;
  departmentIds?: string[];
  departments?: { _id: string; name: string }[];
  days: ShiftDay[];
  dayCutoff?: string;
  lateBufferMinutes?: number;
  earlyLeaveBufferMinutes?: number;
  color?: string;
  notes?: string;
};

/**
 * Atama çakışması — backend 409 ile bu listeyi döndürür.
 *
 * `kind` çakışan kaydın kademesini söyler (plan 2.33): `"definition"` sabit
 * vardiya ya da istisna, `"team"` ekip ataması. `shiftName` kademeye göre
 * vardiyanın ya da ekibin adını taşır, yani modal ikisini de gösterebiliyor.
 */
export type AssignmentConflict = {
  employeeId: string;
  employeeName: string;
  assignmentId: string;
  kind: "definition" | "team";
  shiftDefinitionId: string | null;
  shiftTeamId: string | null;
  /** Vardiya ya da ekip adı, kademeye göre. */
  shiftName: string;
  startDate: string;
  endDate: string | null;
  /** `closePrevious` ile otomatik kapatılabilir mi. */
  closeable: boolean;
};

const cleanParams = (params: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== "")
  );

// ── Vardiya tanımı ────────────────────────────────────────────────────────

export const getShiftDefinitions = async (params: Record<string, any> = {}) => {
  const res = await api.get(`${definitionPath}/list`, {
    params: cleanParams(params),
  });
  return res.data;
};

export const getShiftDefinitionSelectData = async (departmentId?: string) => {
  const res = await api.get(`${definitionPath}/select-data`, {
    params: cleanParams({ departmentId }),
  });
  return res.data;
};

export const getShiftDefinitionById = async (id: string) => {
  const res = await api.get(definitionPath, { params: { _id: id } });
  return res.data;
};

export const createShiftDefinition = async (values: Partial<ShiftDefinition>) => {
  try {
    const res = await api.post(definitionPath, values);
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

export const updateShiftDefinition = async (
  id: string,
  values: Partial<ShiftDefinition>
) => {
  try {
    const res = await api.put(definitionPath, { _id: id, ...values });
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

export const deleteShiftDefinition = async (id: string) => {
  try {
    const res = await api.delete(definitionPath, { data: { _id: id } });
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

// ── Vardiya ataması ───────────────────────────────────────────────────────

/** Bir personelin atamaları. `date` verilirse yalnızca o tarihte geçerli olan. */
export const getEmployeeShiftAssignments = async (
  employeeId: string,
  date?: string
) => {
  const res = await api.get(`${assignmentPath}/list`, {
    params: cleanParams({ employeeId, date }),
  });
  return res.data;
};

/**
 * Şirketteki herkesin o tarihteki düzeni — `employeeId` → vardiya eşlemesi.
 * Atama ekranındaki "Vardiya" kolonu bunu tek istekte doldurur.
 *
 * Ekip ataması olan satırlarda `teamCode`/`teamName` dolu gelir ve o günün
 * vardiyası (`code`/`name`) **rotadan çözülmüş** hâlde döner; ekip dinlenme
 * dönemindeyse vardiya alanları `null` kalır. Çözüm backend'de yapılıyor —
 * panel rota matematiği içermiyor (21 Ağu kararı).
 */
export const getCompanyShiftsOnDate = async (date: string) => {
  const res = await api.get(`${assignmentPath}/by-date`, { params: { date } });
  return res.data;
};

/** Belirli bir tarihte bu vardiyada çalışanlar. */
export const getShiftEmployeesOnDate = async (
  shiftDefinitionId: string,
  date: string
) => {
  const res = await api.get(`${assignmentPath}/on-date`, {
    params: { shiftDefinitionId, date },
  });
  return res.data;
};

/**
 * Toplu atama — İKİ KİP (plan 2.33).
 *
 * `shiftDefinitionId` → sabit vardiya, ya da rotanın üstüne binen istisna.
 * `shiftTeamId`       → ekip; o günkü vardiya rotadan hesaplanır.
 *
 * ⚠ TAM BİRİ gönderilmeli. Tipi ayrık birlik olarak yazmak bilinçli: ikisini
 * de isteğe bağlı yapsaydık "ikisi birden" ya da "hiçbiri" çağrıları derleme
 * zamanında değil, backend 400'ünde ortaya çıkardı.
 *
 * Çakışma varsa backend 409 döner ve `conflicts` listesi gelir — istek TÜMDEN
 * reddedilir, kısmi atama yazılmaz. Çakışma AYNI KADEME içinde aranır: ekibe
 * atarken kişinin bir günlük istisnası engel olmaz.
 *
 * `closePrevious` ile geçiş yapılır: kişinin önceki düzeni yeni atamanın bir
 * gün öncesinde kapatılır.
 */
export const assignEmployeesToShift = async (payload: {
  employeeIds: string[];
  startDate: string;
  endDate?: string | null;
  notes?: string;
  closePrevious?: boolean;
} & (
  | { shiftDefinitionId: string; shiftTeamId?: never }
  | { shiftTeamId: string; shiftDefinitionId?: never }
)) => {
  try {
    const res = await api.post(assignmentPath, payload);
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

export const updateShiftAssignment = async (
  id: string,
  updates: { startDate?: string; endDate?: string | null; notes?: string }
) => {
  try {
    const res = await api.patch(assignmentPath, { _id: id, ...updates });
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};

export const deleteShiftAssignment = async (id: string) => {
  try {
    const res = await api.delete(assignmentPath, { data: { _id: id } });
    return res.data;
  } catch (error: any) {
    return error?.response?.data ?? { success: false };
  }
};
